/**
 * Branch Context for Local State Management
 *
 * Provides complete CRUD functionality for Branches and Branch Contacts
 * as an independent form system with local state persistence.
 */

import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import type { Branch, BranchFormData } from '@/types/branch.types';
import type {
  BranchContact,
  BranchContactFormData,
} from '@/types/branchContact.types';

// Extended interfaces for local state with timestamps
interface LocalBranch extends Branch {
  createdAt?: string;
  updatedAt?: string;
}

interface LocalBranchContact extends BranchContact {
  createdAt?: string;
  updatedAt?: string;
}

// Action types for reducer
type BranchAction =
  | { type: 'SET_BRANCHES'; payload: LocalBranch[] }
  | { type: 'ADD_BRANCH'; payload: LocalBranch }
  | {
      type: 'UPDATE_BRANCH';
      payload: { id: string; data: Partial<LocalBranch> };
    }
  | { type: 'DELETE_BRANCH'; payload: string }
  | { type: 'SET_BRANCH_CONTACTS'; payload: LocalBranchContact[] }
  | { type: 'ADD_BRANCH_CONTACT'; payload: LocalBranchContact }
  | {
      type: 'UPDATE_BRANCH_CONTACT';
      payload: { id: string; data: Partial<LocalBranchContact> };
    }
  | { type: 'DELETE_BRANCH_CONTACT'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null };

// State interface
interface BranchState {
  branches: LocalBranch[];
  branchContacts: LocalBranchContact[];
  isLoading: boolean;
  error: string | null;
}

// Context interface
interface BranchContextType extends BranchState {
  // Branch CRUD operations
  addBranch: (data: BranchFormData) => Promise<void>;
  updateBranch: (id: string, data: BranchFormData) => Promise<void>;
  deleteBranch: (id: string) => Promise<void>;
  getBranchById: (id: string) => LocalBranch | undefined;

  // Branch Contact CRUD operations
  addBranchContact: (data: BranchContactFormData) => Promise<void>;
  updateBranchContact: (
    id: string,
    data: BranchContactFormData
  ) => Promise<void>;
  deleteBranchContact: (id: string) => Promise<void>;
  getBranchContactById: (id: string) => LocalBranchContact | undefined;
  getBranchContactsByBranchId: (branchId: string) => LocalBranchContact[];

  // Utility functions
  clearError: () => void;
  resetState: () => void;
  loadDemoData: () => void; // Add demo data for testing
}

// Initial state
const initialState: BranchState = {
  branches: [],
  branchContacts: [],
  isLoading: false,
  error: null,
};

// Local storage keys
const STORAGE_KEYS = {
  BRANCHES: 'loomis_branches',
  BRANCH_CONTACTS: 'loomis_branch_contacts',
} as const;

// Reducer function
const branchReducer = (
  state: BranchState,
  action: BranchAction
): BranchState => {
  switch (action.type) {
    case 'SET_BRANCHES':
      return { ...state, branches: action.payload };

    case 'ADD_BRANCH':
      return { ...state, branches: [...state.branches, action.payload] };

    case 'UPDATE_BRANCH':
      return {
        ...state,
        branches: state.branches.map(branch =>
          branch.id === action.payload.id
            ? { ...branch, ...action.payload.data }
            : branch
        ),
      };

    case 'DELETE_BRANCH':
      return {
        ...state,
        branches: state.branches.filter(branch => branch.id !== action.payload),
        // Remove branchId from contacts' branchIds array
        branchContacts: state.branchContacts
          .map(contact => ({
            ...contact,
            branchIds: contact.branchIds.filter(id => id !== action.payload),
          }))
          // Remove contacts that no longer have any branches
          .filter(contact => contact.branchIds.length > 0),
      };

    case 'SET_BRANCH_CONTACTS':
      return { ...state, branchContacts: action.payload };

    case 'ADD_BRANCH_CONTACT':
      return {
        ...state,
        branchContacts: [...state.branchContacts, action.payload],
      };

    case 'UPDATE_BRANCH_CONTACT':
      return {
        ...state,
        branchContacts: state.branchContacts.map(contact =>
          contact.id === action.payload.id
            ? { ...contact, ...action.payload.data }
            : contact
        ),
      };

    case 'DELETE_BRANCH_CONTACT':
      return {
        ...state,
        branchContacts: state.branchContacts.filter(
          contact => contact.id !== action.payload
        ),
      };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    default:
      return state;
  }
};

// Helper functions
const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const saveToLocalStorage = (key: string, data: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error(`Error saving to localStorage:`, error);
  }
};

const loadFromLocalStorage = (key: string): unknown => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error(`Error loading from localStorage:`, error);
    return null;
  }
};

// Create context
const BranchContext = createContext<BranchContextType | undefined>(undefined);

// Provider component
export const BranchProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(branchReducer, initialState);
  const { toast } = useToast();

  // Migration function to convert old contact data format to new format
  const migrateContactData = (
    contacts: (LocalBranchContact | Record<string, unknown>)[]
  ): LocalBranchContact[] => {
    return contacts.map(contact => {
      // Cast to LocalBranchContact for known properties
      const typedContact = contact as LocalBranchContact & {
        branchId?: string;
      };

      // Check if contact has old branchId format (string) instead of branchIds (array)
      if (
        'branchId' in contact &&
        typedContact.branchId &&
        !('branchIds' in contact)
      ) {
        return {
          ...typedContact,
          branchIds: [typedContact.branchId], // Convert single branchId to array
          branchId: undefined, // Remove old property
        } as LocalBranchContact;
      }
      // Ensure branchIds is always an array
      if (!typedContact.branchIds || !Array.isArray(typedContact.branchIds)) {
        console.warn(
          `Contact ${typedContact.id} has invalid branchIds, skipping...`
        );
        return {
          ...typedContact,
          branchIds: [],
        } as LocalBranchContact;
      }
      return typedContact;
    });
  };

  // Load data from localStorage on mount
  useEffect(() => {
    const savedBranches = loadFromLocalStorage(STORAGE_KEYS.BRANCHES) as
      LocalBranch[] | null;
    const savedContactsRaw = loadFromLocalStorage(
      STORAGE_KEYS.BRANCH_CONTACTS
    ) as (LocalBranchContact | Record<string, unknown>)[] | null;

    if (savedBranches) {
      dispatch({ type: 'SET_BRANCHES', payload: savedBranches });
    }
    if (savedContactsRaw) {
      // Migrate old contact data to new format
      const migratedContacts = migrateContactData(savedContactsRaw);
      dispatch({ type: 'SET_BRANCH_CONTACTS', payload: migratedContacts });
      // Save migrated data back to localStorage
      saveToLocalStorage(STORAGE_KEYS.BRANCH_CONTACTS, migratedContacts);
    }
  }, []);

  // Save to localStorage whenever state changes
  useEffect(() => {
    if (state.branches.length > 0) {
      saveToLocalStorage(STORAGE_KEYS.BRANCHES, state.branches);
    }
  }, [state.branches]);

  useEffect(() => {
    if (state.branchContacts.length > 0) {
      saveToLocalStorage(STORAGE_KEYS.BRANCH_CONTACTS, state.branchContacts);
    }
  }, [state.branchContacts]);

  // Branch CRUD operations
  const addBranch = async (data: BranchFormData): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const newBranch: LocalBranch = {
        id: generateId(),
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      dispatch({ type: 'ADD_BRANCH', payload: newBranch });

      toast({
        title: 'Erfolg',
        description: 'Filiale wurde erfolgreich erstellt.',
      });
    } catch (error) {
      const errorMessage = 'Fehler beim Erstellen der Filiale.';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast({
        title: 'Fehler',
        description: errorMessage,
        variant: 'destructive',
      });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateBranch = async (
    id: string,
    data: BranchFormData
  ): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const updateData = {
        ...data,
        updatedAt: new Date().toISOString(),
      };

      dispatch({ type: 'UPDATE_BRANCH', payload: { id, data: updateData } });

      toast({
        title: 'Erfolg',
        description: 'Filiale wurde erfolgreich aktualisiert.',
      });
    } catch (error) {
      const errorMessage = 'Fehler beim Aktualisieren der Filiale.';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast({
        title: 'Fehler',
        description: errorMessage,
        variant: 'destructive',
      });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const deleteBranch = async (id: string): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      dispatch({ type: 'DELETE_BRANCH', payload: id });

      toast({
        title: 'Erfolg',
        description: 'Filiale wurde erfolgreich gelöscht.',
      });
    } catch (error) {
      const errorMessage = 'Fehler beim Löschen der Filiale.';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast({
        title: 'Fehler',
        description: errorMessage,
        variant: 'destructive',
      });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const getBranchById = (id: string): LocalBranch | undefined => {
    return state.branches.find(branch => branch.id === id);
  };

  // Branch Contact CRUD operations
  const addBranchContact = async (
    data: BranchContactFormData
  ): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      // Validate that at least one branch is selected
      if (!data.branchIds || data.branchIds.length === 0) {
        throw new Error('Mindestens eine Filiale muss ausgewählt werden.');
      }

      // Validate that all selected branches exist
      const invalidBranches = data.branchIds.filter(
        branchId => !getBranchById(branchId)
      );
      if (invalidBranches.length > 0) {
        throw new Error(
          'Eine oder mehrere ausgewählte Filialen existieren nicht.'
        );
      }

      const newContact: LocalBranchContact = {
        id: generateId(),
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      dispatch({ type: 'ADD_BRANCH_CONTACT', payload: newContact });

      toast({
        title: 'Erfolg',
        description: 'Kontaktperson wurde erfolgreich erstellt.',
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Fehler beim Erstellen der Kontaktperson.';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast({
        title: 'Fehler',
        description: errorMessage,
        variant: 'destructive',
      });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateBranchContact = async (
    id: string,
    data: BranchContactFormData
  ): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      // Validate that at least one branch is selected
      if (!data.branchIds || data.branchIds.length === 0) {
        throw new Error('Mindestens eine Filiale muss ausgewählt werden.');
      }

      // Validate that all selected branches exist
      const invalidBranches = data.branchIds.filter(
        branchId => !getBranchById(branchId)
      );
      if (invalidBranches.length > 0) {
        throw new Error(
          'Eine oder mehrere ausgewählte Filialen existieren nicht.'
        );
      }

      const updateData = {
        ...data,
        updatedAt: new Date().toISOString(),
      };

      dispatch({
        type: 'UPDATE_BRANCH_CONTACT',
        payload: { id, data: updateData },
      });

      toast({
        title: 'Erfolg',
        description: 'Kontaktperson wurde erfolgreich aktualisiert.',
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Fehler beim Aktualisieren der Kontaktperson.';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast({
        title: 'Fehler',
        description: errorMessage,
        variant: 'destructive',
      });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const deleteBranchContact = async (id: string): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      dispatch({ type: 'DELETE_BRANCH_CONTACT', payload: id });

      toast({
        title: 'Erfolg',
        description: 'Kontaktperson wurde erfolgreich gelöscht.',
      });
    } catch (error) {
      const errorMessage = 'Fehler beim Löschen der Kontaktperson.';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast({
        title: 'Fehler',
        description: errorMessage,
        variant: 'destructive',
      });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const getBranchContactById = (id: string): LocalBranchContact | undefined => {
    return state.branchContacts.find(contact => contact.id === id);
  };

  const getBranchContactsByBranchId = (
    branchId: string
  ): LocalBranchContact[] => {
    return state.branchContacts.filter(contact =>
      contact.branchIds.includes(branchId)
    );
  };

  // Utility functions
  const clearError = (): void => {
    dispatch({ type: 'SET_ERROR', payload: null });
  };

  const resetState = (): void => {
    dispatch({ type: 'SET_BRANCHES', payload: [] });
    dispatch({ type: 'SET_BRANCH_CONTACTS', payload: [] });
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_LOADING', payload: false });

    // Clear localStorage
    localStorage.removeItem(STORAGE_KEYS.BRANCHES);
    localStorage.removeItem(STORAGE_KEYS.BRANCH_CONTACTS);
  };

  const loadDemoData = (): void => {
    const demoBranches: LocalBranch[] = [
      {
        id: 'branch-demo-1',
        language: 'de-CH',
        name: 'Hauptfiliale Zürich',
        country: 'CH',
        street: 'Bahnhofstrasse',
        number: '15',
        zipCode: '8001',
        city: 'Zürich',
        canton: 'ZH',
        openingHours: {
          monday: {
            enabled: true,
            workingStart: '08:00',
            workingEnd: '18:00',
            breakStart: '12:00',
            breakEnd: '13:00',
          },
          friday: {
            enabled: true,
            workingStart: '08:00',
            workingEnd: '17:00',
          },
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: 'branch-demo-2',
        language: 'fr-CH',
        name: 'Filiale Genève',
        country: 'CH',
        street: 'Rue du Rhône',
        number: '42',
        zipCode: '1204',
        city: 'Genève',
        canton: 'GE',
        openingHours: {
          tuesday: {
            enabled: true,
            workingStart: '09:00',
            workingEnd: '17:30',
          },
          wednesday: {
            enabled: true,
            workingStart: '09:00',
            workingEnd: '17:30',
          },
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ];

    const demoContacts: LocalBranchContact[] = [
      {
        id: 'contact-demo-1',
        language: 'de-CH',
        function: 'Filialleiter',
        isRepresentative: true,
        address: 'Herr',
        firstName: 'Max',
        lastName: 'Mustermann',
        email: 'max.mustermann@loomis.ch',
        phone: '+41 44 123 45 67',
        mobile: '+41 79 123 45 67',
        branchIds: ['branch-demo-1'], // Contact assigned to Zürich branch
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: 'contact-demo-2',
        language: 'fr-CH',
        function: 'Chef de succursale',
        isRepresentative: true,
        address: 'Madame',
        firstName: 'Marie',
        lastName: 'Dubois',
        email: 'marie.dubois@loomis.ch',
        phone: '+41 22 987 65 43',
        branchIds: ['branch-demo-2'], // Contact assigned to Genève branch
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: 'contact-demo-3',
        language: 'de-CH',
        function: 'Regional Manager',
        isRepresentative: false,
        address: 'Herr',
        firstName: 'Hans',
        lastName: 'Weber',
        email: 'hans.weber@loomis.ch',
        phone: '+41 44 555 66 77',
        mobile: '+41 79 555 66 77',
        branchIds: ['branch-demo-1', 'branch-demo-2'], // Contact assigned to multiple branches
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ];

    dispatch({ type: 'SET_BRANCHES', payload: demoBranches });
    dispatch({ type: 'SET_BRANCH_CONTACTS', payload: demoContacts });

    toast({
      title: 'Demo-Daten geladen',
      description: 'Beispieldaten für Filialen und Kontakte wurden erstellt.',
    });
  };

  const contextValue: BranchContextType = {
    ...state,
    addBranch,
    updateBranch,
    deleteBranch,
    getBranchById,
    addBranchContact,
    updateBranchContact,
    deleteBranchContact,
    getBranchContactById,
    getBranchContactsByBranchId,
    clearError,
    resetState,
    loadDemoData,
  };

  return (
    <BranchContext.Provider value={contextValue}>
      {children}
    </BranchContext.Provider>
  );
};

// Custom hook to use the context
// eslint-disable-next-line react-refresh/only-export-components
export const useBranchContext = (): BranchContextType => {
  const context = useContext(BranchContext);
  if (context === undefined) {
    throw new Error('useBranchContext must be used within a BranchProvider');
  }
  return context;
};

export default BranchContext;
